import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

import { find, isEqual, keys } from 'lodash';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';
import { useHistory } from 'react-router-dom';

import { ActionId } from 'admin/actions/Actions';
import { FeatureFlagId } from 'admin/feature-flags/FeatureFlags';
import { ModuleId } from 'admin/modules/Modules';
import { SourceId } from 'admin/sources/Sources';
import { isAuthorized } from 'auth/Auth';
import { SystemLabel } from 'common/comments2/Comments2.types';
import { MessageActionType, useMessage } from 'common/message-bar/MessageBar';
import { Overlay } from 'core/overlay/Overlay';
import { usePrevious } from 'core/util/Hook';
import { Route } from 'core/util/Route';
import { DropdownAction, ReviewActionsMenu } from 'reviews2/feed/review-yeti/actions/ActionsMenu';
import { useReview, ReviewActionType } from 'reviews2/feed/review-yeti/Review.store';
import { ContextProps, ReadOnlyReason } from 'reviews2/feed/review-yeti/Review.types';
import { setPublisherConfig } from 'reviews2/feed/review-yeti/Review.util';
import { toggleLabel } from 'reviews2/Reviews.api';
import { fetchAuthorizedSources } from 'social/publisher/PublisherFetchers';

import 'reviews2/feed/review-yeti/actions-container/ActionsMenuContainer.scss';

enum ReviewActionId {
    Read = 1,
    Flag,
    Share,
    Forward,
    Feedback,
    CreateTicket,
    Publish,
    RemoveFromSource,
    RemoveNeedsResponse,
    RemoveNoResponseRequired,
}

export const ReviewActionsMenuContainer: FunctionComponent<ContextProps> = ({ context }) => {
    const { t } = useTranslation('reviews2');
    const history = useHistory();
    const [{ review, isNorthStarEnabled }, dispatch] = useReview();
    const { options, handlers, config, profile } = context;
    const previousLabels = usePrevious(review?.r4e_label_ids);
    const [isRead, setIsRead] = useState<boolean>();
    const [isFlagged, setIsFlagged] = useState<boolean>();
    const [isPublished, setIsPublished] = useState<boolean>();
    const [isNoResponseRequired, setIsNoResponseRequired] = useState<boolean>();
    const [, dispatchMessage] = useMessage();
    const OVERLAY_AREA = useMemo(() => `actions-menu-container-${review?.document_id}`, [review]);

    useEffect(() => {
        if (!isEqual(review?.r4e_label_ids, previousLabels)) {
            const read = review?.r4e_label_ids?.includes(SystemLabel.Read);
            const flagged = review?.r4e_label_ids?.includes(SystemLabel.Flagged);
            const published = review?.r4e_label_ids?.includes(SystemLabel.Published);
            const noResponseRequired = review?.r4e_label_ids?.includes(SystemLabel.NoResponseRequired);

            setIsRead(read);
            setIsFlagged(flagged);
            setIsPublished(published);
            setIsNoResponseRequired(noResponseRequired);
        }
    }, [review, previousLabels]);

    const share = useCallback(
        async (review: any) => {
            const { locationSources } = await trackPromise(
                fetchAuthorizedSources(review.r4e_tenant_id, { filters: [] }),
                OVERLAY_AREA,
            );
            const locationSource = find(locationSources, { lUid: review.location?.uid });
            const publishableSourceIds = keys(locationSource?.sources);
            const hasPublishableSourceIds = publishableSourceIds.length > 0;
            const hasPublishModule = profile?.tenant?.moduleIDs.includes(ModuleId.SocialPublishing);

            if (hasPublishableSourceIds && hasPublishModule) {
                let url = `${config?.urls?.app}${Route.Social2}/publish-nuvi?route=post-management&review_id=${review.document_id}`;

                if (review.location?.uid) {
                    url += `&locationUid=${review.location?.uid}`;
                    window.location.href = url;
                }
            } else if (hasPublishableSourceIds) {
                const returnUrl = history.createHref(window.location);
                let text = t('SHARE_REVIEW', { source: review.source?.name });

                if (review.author_real_name) {
                    text += t('SHARE_REVIEW_W_REVIEWER', { reviewer: review.author_real_name });
                }

                setPublisherConfig(review, text.trim(), returnUrl);
                history.push(`${Route.Social2}/publisher`);
            } else {
                dispatchMessage({
                    type: MessageActionType.ShowWarning,
                    text: t('SHARE_REVIEW_DISABLED'),
                });
            }
        },
        [history, t, dispatchMessage, OVERLAY_AREA, config, profile],
    );

    const actionFn = useCallback(
        async (event, action) => {
            event?.stopPropagation();

            let updatedReview;

            switch (action.id) {
                case ReviewActionId.Read: {
                    setIsRead(!isRead);
                    updatedReview = await trackPromise(
                        toggleLabel(review!.r4e_tenant_id, review!, SystemLabel.Read),
                        OVERLAY_AREA,
                    );
                    break;
                }
                case ReviewActionId.Flag: {
                    setIsFlagged(!isFlagged);
                    updatedReview = await trackPromise(
                        toggleLabel(review!.r4e_tenant_id, review!, SystemLabel.Flagged),
                        OVERLAY_AREA,
                    );
                    break;
                }
                case ReviewActionId.Publish: {
                    setIsPublished(!isPublished);
                    updatedReview = await trackPromise(
                        toggleLabel(review!.r4e_tenant_id, review!, SystemLabel.Published),
                        OVERLAY_AREA,
                    );
                    break;
                }
                case ReviewActionId.RemoveNeedsResponse: {
                    updatedReview = await trackPromise(
                        toggleLabel(review!.r4e_tenant_id, review!, SystemLabel.NeedsResponse),
                        OVERLAY_AREA,
                    );
                    break;
                }
                case ReviewActionId.Share: {
                    share(review!);
                    break;
                }
                case ReviewActionId.Forward: {
                    handlers?.forwardReview?.(review!);
                    break;
                }
                case ReviewActionId.Feedback: {
                    handlers?.provideFeedback?.(review!);
                    break;
                }
                case ReviewActionId.CreateTicket: {
                    handlers?.createTicket?.(review!);
                    break;
                }
                case ReviewActionId.RemoveFromSource: {
                    updatedReview = await trackPromise(
                        toggleLabel(review!.r4e_tenant_id, review!, SystemLabel.Syndicated),
                        OVERLAY_AREA,
                    );
                    break;
                }
                case ReviewActionId.RemoveNoResponseRequired: {
                    updatedReview = await trackPromise(
                        toggleLabel(review!.r4e_tenant_id, review!, SystemLabel.NoResponseRequired),
                        OVERLAY_AREA,
                    );
                    break;
                }
                default:
                    throw new TypeError(`actionFn: unhandled action: ${action.id}`);
            }

            if (updatedReview) {
                dispatch({
                    type: ReviewActionType.UpdateReview,
                    review: {
                        // only update labelIds
                        r4e_label_ids: updatedReview.r4e_label_ids,
                    },
                });
            }
        },
        [review, dispatch, isRead, isFlagged, isPublished, share, handlers, OVERLAY_AREA],
    );
    const actions = useMemo(() => {
        const menuActions: DropdownAction[] = [];

        if (options!.readOnly && [ReadOnlyReason.Reports, ReadOnlyReason.View].includes(options?.readOnlyReason!)) {
            return menuActions;
        }

        if (isAuthorized({ actions: [ActionId.Reviews.Read] }) && !options?.actionOptions?.hideMarkAsRead) {
            menuActions.push({
                id: ReviewActionId.Read,
                icon: 'icon-check',
                label: isRead ? t('MARK_UNREAD') : t('MARK_READ'),
            });
        }

        if (isAuthorized({ actions: [ActionId.Reviews.Flag] }) && !options?.actionOptions?.hideFlag) {
            menuActions.push({
                id: ReviewActionId.Flag,
                icon: 'icon-flag',
                label: isFlagged ? t('UNFLAG') : t('FLAG'),
            });
        }

        if (
            isAuthorized({ actions: [ActionId.Reviews.Publish] }) &&
            !options?.actionOptions?.hidePublish &&
            review.source?.publishable
        ) {
            menuActions.push({
                id: ReviewActionId.Publish,
                icon: 'icon-globe',
                label: isPublished ? t('UNPUBLISH_REVIEW') : t('PUBLISH_REVIEW'),
            });
        }

        if (
            review?.r4e_label_ids?.includes(SystemLabel.NeedsResponse) &&
            !options?.actionOptions?.hideRemoveNeedsResponse
        ) {
            menuActions.push({
                id: ReviewActionId.RemoveNeedsResponse,
                icon: 'icon-exclamation-triangle',
                label: t('REMOVE_NEEDS_RESPONSE'),
            });
        }

        if (isAuthorized({ actions: [ActionId.SocialPublishPosts.Add] }) && !options?.actionOptions?.hideShare) {
            menuActions.push({
                id: ReviewActionId.Share,
                icon: 'icon-share-alt',
                label: t('SHARE'),
            });
        }

        if (
            (isAuthorized({ actions: [ActionId.Reviews.ForwardToRole] }) ||
                isAuthorized({ actions: [ActionId.Reviews.ForwardToUserOrEmail] })) &&
            !options?.actionOptions?.hideForward
        ) {
            menuActions.push({
                id: ReviewActionId.Forward,
                icon: 'icon-paper-plane',
                label: t('FORWARD'),
            });
        }

        if (
            review?.taxonomy_categories?.length &&
            isAuthorized({ actions: [ActionId.Reviews.View] }) &&
            !options?.actionOptions?.hideFeedback
        ) {
            menuActions.push({
                id: ReviewActionId.Feedback,
                icon: 'icon-comment-check',
                label: t('PROVIDE_FEEDBACK'),
            });
        }

        if (
            isAuthorized({ actions: [ActionId.Tickets.Add] }) &&
            !options?.actionOptions?.hideCreateTicket &&
            !context.reviewTicket
        ) {
            menuActions.push({
                id: ReviewActionId.CreateTicket,
                icon: 'icon-ticket',
                label: t('CREATE_TICKET'),
            });
        }

        if (review?.r4e_label_ids?.includes(SystemLabel.Syndicated) && review?.source?.id === SourceId.HealthGrades) {
            menuActions.push({
                id: ReviewActionId.RemoveFromSource,
                icon: 'icon-trash-alt',
                label: t('REMOVE_FROM_SOURCE'),
            });
        }

        if (isAuthorized({ featureFlags: [FeatureFlagId.Reviews3NoResponseRequired] })) {
            menuActions.push({
                id: ReviewActionId.RemoveNoResponseRequired,
                icon: 'icon-exclamation-triangle',
                label: isNoResponseRequired ? t('REMOVE_NO_RESPONSE_REQUIRED') : t('MARK_NO_RESPONSE_REQUIRED'),
            });
        }

        return menuActions;
    }, [
        options,
        review?.source?.publishable,
        review?.taxonomy_categories?.length,
        review?.r4e_label_ids,
        review?.source?.id,
        context.reviewTicket,
        isRead,
        t,
        isFlagged,
        isPublished,
        isNoResponseRequired,
    ]);

    return (
        <div className="r4e-reviews2-actions-menu-container">
            <ReviewActionsMenu isNorthStarEnabled={isNorthStarEnabled} actions={actions} actionFn={actionFn}>
                <Overlay area={OVERLAY_AREA} />
            </ReviewActionsMenu>
        </div>
    );
};
